<template>
    <div>
        <canvas ref="canvas" class="sig-canvas"
            @pointerdown="down" @pointermove="move" @pointerup="up" @pointerleave="up"></canvas>
        <div class="text-right">
            <v-btn small text @click="clear">ล้าง</v-btn>
        </div>
    </div>
</template>
<script>
export default {
    data () { return { drawing: false, dirty: false, ctx: null, width: 0 } },
    methods: {
        // The canvas is sized in device pixels and scaled back down, or the
        // signature is a blurry mess on a phone.
        //
        // `force` skips the same-width short-circuit below AND the bitmap
        // snapshot/redraw further down -- only clear() passes it, because
        // clear() means "wipe this on purpose, ink included", not "the layout
        // changed, keep what's drawn".
        resize (force) {
            const canvas = this.$refs.canvas
            const ratio = window.devicePixelRatio || 1
            const width = canvas.clientWidth
            const height = 180
            // Android's soft keyboard opening under the ชื่อผู้เซ็น field --
            // and the URL bar hiding on scroll -- both fire `window.resize`
            // with the canvas's on-screen WIDTH unchanged, only the viewport's
            // height. Resizing the <canvas> element clears its bitmap, which
            // silently replaced a customer's just-drawn signature with a blank
            // white PNG (isEmpty() only looks at `dirty`, which nothing but
            // clear() may reset). So a same-width call touches nothing.
            if (!force && this.ctx && width === this.width) return
            // A genuine width change (rotation, split-screen) DOES need to
            // resize the bitmap -- <canvas> has no reflow-preserving resize --
            // so what's currently drawn is captured first and redrawn scaled
            // into the new size, rather than lost. `force` (clear(), and the
            // finish-sheet reopening) means the opposite: the caller wants the
            // ink gone, not preserved -- restoring it here from the snapshot
            // would make ล้าง a no-op, and would carry one customer's
            // signature into the next customer's finish sheet on reopen.
            let snapshot = null
            if (!force && this.ctx && width && this.width) {
                snapshot = document.createElement('canvas')
                snapshot.width = canvas.width
                snapshot.height = canvas.height
                snapshot.getContext('2d').drawImage(canvas, 0, 0)
            }
            canvas.width = width * ratio
            canvas.height = height * ratio
            canvas.style.height = height + 'px'
            this.ctx = canvas.getContext('2d')
            this.ctx.scale(ratio, ratio)
            this.ctx.lineWidth = 2
            this.ctx.lineCap = 'round'
            this.ctx.strokeStyle = '#000'
            this.ctx.fillStyle = '#fff'
            this.ctx.fillRect(0, 0, width, height)
            if (snapshot) {
                // Drawn through the same scaled context as fillRect above, so
                // this is in CSS-pixel space like the rest of this method --
                // drawImage stretches the old bitmap into the new width.
                this.ctx.drawImage(snapshot, 0, 0, snapshot.width, snapshot.height, 0, 0, width, height)
            }
            this.width = width
            // `dirty` is deliberately untouched here -- only clear() may reset it.
        },
        point (e) {
            const rect = this.$refs.canvas.getBoundingClientRect()
            return { x: e.clientX - rect.left, y: e.clientY - rect.top }
        },
        down (e) {
            this.drawing = true
            const p = this.point(e)
            this.ctx.beginPath()
            this.ctx.moveTo(p.x, p.y)
            // Capture the pointer so a finger that slides off the canvas mid-stroke
            // does not leave the pad stuck in a drawing state.
            if (this.$refs.canvas.setPointerCapture) this.$refs.canvas.setPointerCapture(e.pointerId)
        },
        move (e) {
            if (!this.drawing) return
            // Set on an actual stroke, not on pointerdown: a stray tap that
            // never moves must stay isEmpty(), or it silently overrides a
            // driver's refusedReason choice (finish() treats hasSignature as
            // "the customer signed") and records a blank image as a signature.
            this.dirty = true
            const p = this.point(e)
            this.ctx.lineTo(p.x, p.y)
            this.ctx.stroke()
        },
        up () { this.drawing = false },
        clear () { this.dirty = false; this.resize(true) },
        isEmpty () { return !this.dirty },
        toDataUrl () { return this.$refs.canvas.toDataURL('image/png') }
    },
    mounted () {
        this.resize()
        // Not `window.addEventListener('resize', this.resize)` directly: the
        // listener is called with the native Event as the first argument,
        // which would be passed through as `force` -- a truthy object -- and
        // defeat the same-width short-circuit above on every single resize.
        this._onResize = () => this.resize()
        window.addEventListener('resize', this._onResize)
    },
    beforeDestroy () { window.removeEventListener('resize', this._onResize) }
}
</script>
<style scoped>
/* Without touch-action:none, iOS Safari scrolls the page under the customer's
   finger instead of drawing. */
.sig-canvas {
    width: 100%;
    border: 1px solid #ccc;
    border-radius: 4px;
    touch-action: none;
    display: block;
}
</style>
